import { useHistory } from 'react-router-dom'
import { findIndex } from 'lodash'
import {
  setLesson,
  setShowAuthModal,
  updateCourseLesson,
  updateLesson,
  useAppDispatch,
  useAppSelector
} from '../../redux'
import { LessonType } from '../../types'
import { ROUTES } from '..'
import { getCourseCheckout, getCourseLessons, useLesson, useSegment } from '.'

export const useCourse = () => {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const { track } = useSegment()
  const { getNextLesson, handleComplete, handleSkip } = useLesson()
  const { user } = useAppSelector((state) => state.user)
  const { lesson, course } = useAppSelector((state) => state.course)

  // Toggles the designated lesson complete or uncomplete
  const toggleLessonComplete = async (l?: LessonType, moveToNext?: boolean) => {
    if (!course || !l) {
      return
    }

    const curr = l._id === lesson?._id
    const next = getNextLesson(l._id, course)
    const isCompleted = !l.isCompleted

    if (curr) {
      dispatch(updateLesson({ isCompleted }))
    }

    dispatch(
      updateCourseLesson({
        _id: l._id,
        lesson: { isCompleted }
      })
    )

    if (curr && isCompleted && next && moveToNext) {
      dispatch(setLesson(next))
      history.push(ROUTES.course.byLesson(course.slug, next.slug))
    }

    await handleComplete(l)
    await checkForSkippedLessons(l)
  }

  // Identifies and skips any applicable lessons before the designated lesson
  const checkForSkippedLessons = async (lesson: LessonType) => {
    if (!course?.modules) {
      return
    }

    const lessons = getCourseLessons(course)
    const lessonIndex = findIndex(lessons, (l) => l._id === lesson._id)
    const lessonsToSkip = lessons.slice(0, lessonIndex)

    await Promise.all(
      lessonsToSkip.map(async (l) => {
        await handleSkip(l)

        if (lesson._id === l._id) {
          dispatch(
            updateLesson({
              isSkipped: true
            })
          )
        }

        dispatch(
          updateCourseLesson({
            _id: l._id,
            lesson: { isSkipped: true }
          })
        )
      })
    )
  }

  const checkout = () => {
    if (!course) {
      return
    }

    track('Clicked Checkout Button', {
      course_id: course._id,
      course_title: course.title,
      logged_in: !!user
    })

    const redirect = [
      `/checkout/${getCourseCheckout(course, user)}`,
      `?courseName=${encodeURIComponent(course.title)}`,
      `&courseId=${encodeURIComponent(course._id)}`,
      `&courseSlug=${encodeURIComponent(course.slug)}`
    ].join('')

    if (!user) {
      return dispatch(setShowAuthModal({ show: true, redirect }))
    }

    history.push(redirect)
  }

  return {
    toggleLessonComplete,
    checkForSkippedLessons,
    checkout
  }
}
